import { Tooltip, useDisclosure, useTheme } from "@chakra-ui/react";
import React from "react";

import { Button, IconButton } from "../../../../components";
import { canManageTraining } from "../../../../utils/permissions";
import useCurrentUser from "../../../hooks/useCurrentUser";
import AddToTrainingProgramIcon from "../../AddToTrainingProgramModal/AddToTrainingProgramIcon";
import AddToTrainingProgramModal from "../../AddToTrainingProgramModal/AddToTrainingProgramModal";

interface AddToTrainingProgramButtonProps {
  callId: string;
  clipIds?: Array<string>;
  size: "md" | "sm";
  dataTestId?: string;
  variant?: "light";
  buttonText?: string;
  hideTooltip?: boolean;
}

const AddToTrainingProgramButton: React.FC<AddToTrainingProgramButtonProps> = ({
  callId,
  size,
  dataTestId,
  clipIds,
  variant,
  buttonText = "Add to",
  hideTooltip = false,
}) => {
  const addToTrainingProgramModal = useDisclosure();
  const { colors } = useTheme();
  const currentUser = useCurrentUser();

  if (!canManageTraining(currentUser)) {
    return null;
  }

  return (
    <>
      {size === "md" && (
        <Tooltip label="Add to a training program" isDisabled={hideTooltip}>
          <Button
            data-testid={dataTestId || "add-to-training-program-button"}
            onClick={addToTrainingProgramModal.onOpen}
            cursor="pointer"
            color="blue.600"
            fontWeight="500"
            px="3"
            py="1.5"
            alignItems="center"
            variant="chalk"
            size="sm"
            onFocus={(e) => e.preventDefault()}
            _hover={{ bg: variant === "light" ? "gray.50" : "gray.100" }}
            textTransform="none"
          >
            <AddToTrainingProgramIcon
              mr="2"
              color={colors.blue[600]}
              w="18px"
              h="18px"
            />
            {buttonText}
          </Button>
        </Tooltip>
      )}
      {size === "sm" && (
        <Tooltip
          label="Add to a training program"
          placement="bottom-start"
          isDisabled={hideTooltip}
        >
          <IconButton
            data-testid={dataTestId} // small buttons occur in lists and need external position
            variant="icon"
            aria-label="View highlights"
            onFocus={(e) => e.preventDefault()}
            ml={2}
            icon={
              <AddToTrainingProgramIcon
                strokeWidth="1.5px"
                color={colors.gray[500]}
                w="18px"
                h="18px"
              />
            }
            onClick={addToTrainingProgramModal.onOpen}
          />
        </Tooltip>
      )}
      {addToTrainingProgramModal.isOpen && (
        <AddToTrainingProgramModal
          onClose={addToTrainingProgramModal.onClose}
          callId={callId}
          clipIds={clipIds}
        />
      )}
    </>
  );
};

export default AddToTrainingProgramButton;
